/**
 * Aperçu du rapport : **une action entière**, montrée telle qu'elle est écrite.
 *
 * L'aperçu se réduisait à huit cartes floutées portant des titres génériques et
 * figés — « Compétence à acquérir en priorité », « L'automatisation à installer
 * en premier ». Ce sont des intitulés de rubrique, pas du contenu : on vendait
 * un document de dix demi-pages avec ce qui ressemblait à un sommaire, et
 * l'acheteur n'avait aucun moyen d'estimer ce qu'il achetait.
 *
 * Le rapport réel est produit après paiement et pour la personne : il ne peut
 * pas être montré d'avance. Ce qui peut l'être, c'est **un spécimen complet**,
 * écrit au format exact du livrable — les six rubriques, la longueur réelle.
 * Il est signalé comme exemple, sans quoi ce serait le faux signal que la règle
 * non négociable n° 6 interdit.
 */

const ETAPES: ReadonlyArray<string> = [
  "Exportez du logiciel comptable le relevé du mois et le grand livre du compte banque, en CSV. Retirez les colonnes qui portent des noms de clients ou de salariés : l'assistant n'en a pas besoin pour rapprocher des montants et des dates.",
  "Donnez-lui les deux fichiers dans le même message, en précisant la devise, le format de date utilisé et le seuil d'écart en dessous duquel vous ne voulez pas être alerté — sans quoi il vous signalera des différences de vingt francs.",
  "Demandez trois listes séparées et non un commentaire : les lignes rapprochées avec certitude, les lignes rapprochées sous réserve, et les lignes sans correspondance. C'est la troisième qui contient votre travail réel.",
  "Reprenez la deuxième liste ligne à ligne. C'est là que se logent les erreurs de l'assistant : deux montants identiques à quelques jours d'intervalle, un virement scindé, un libellé tronqué par l'export.",
  "Reportez vos décisions dans le logiciel vous-même. L'assistant prépare, il ne saisit pas : une écriture passée sans qu'un humain l'ait décidée est une écriture que personne ne peut justifier en contrôle.",
];

export function ApercuAction() {
  return (
    <div className="apercu reveal">
      <div className="apercu-head">
        <span>Aperçu du rapport · action 01 sur 10, en entier</span>
        <b>exemple — métier : comptable</b>
      </div>
      <p className="fine">
        Cet exemple est celui d'un comptable. La vôtre sera écrite avec les mots de votre métier,
        et vous en verrez le début gratuitement dès que vous aurez votre indice.
      </p>

      <h3>
        Faire préparer vos rapprochements bancaires par un assistant, sans jamais lui confier la
        validation
      </h3>

      <div className="apercu-part">
        <div className="k">Pourquoi celle-ci d'abord</div>
        <p>
          C'est votre tâche la plus exposée et la plus chronophage à la fois : un rapprochement
          mensuel vous prend six à dix heures, et l'essentiel de ce temps se passe à comparer deux
          colonnes de chiffres — exactement ce qu'une machine fait sans se fatiguer. Le jugement,
          lui, ne porte que sur les lignes qui ne tombent pas juste : elles représentent moins d'un
          dixième du volume et la totalité de la valeur de votre intervention. Commencer par là vous
          rend visible sur la partie que personne ne peut automatiser.
        </p>
      </div>

      {/*
        La suite de l'action se déplie — 02/10/2026. Montrée en entier, elle
        prenait trois écrans et demi de téléphone au milieu de l'accueil, avant
        le prix. Le titre et le « pourquoi » suffisent à juger du ton ; qui veut
        juger de la profondeur ouvre la suite, qui reste dans la page, entière.
      */}
      <details className="deplie">
        <summary>Voir la suite : les étapes, le texte à copier, le piège</summary>
      <div className="apercu-part">
        <div className="k">Les étapes, dans l'ordre</div>
        <ol className="apercu-etapes">
          {ETAPES.map((etape) => (
            <li key={etape}>{etape}</li>
          ))}
        </ol>
      </div>

      <div className="apercu-part">
        <div className="k">Le prompt à reprendre tel quel</div>
        <pre className="apercu-prompt">
{`Tu es assistant d'un comptable. Voici deux extraits : un relevé
bancaire et un grand livre du compte banque, même période, en francs
CFA, dates au format JJ/MM/AAAA.

Rapproche-les. Rends exactement trois listes, sans commentaire :
1. RAPPROCHÉ — montant, date relevé, date livre, écart en jours
2. SOUS RÉSERVE — même chose, plus la raison du doute en une phrase
3. SANS CORRESPONDANCE — la ligne, et de quel document elle vient

Ignore les écarts inférieurs à 100 F. N'invente aucune ligne : si un
montant n'a pas de contrepartie, il va en liste 3. Ne propose aucune
écriture de régularisation.`}
        </pre>
      </div>

      <div className="apercu-part">
        <div className="k">Le piège</div>
        <p>
          Un assistant qui ne trouve pas de correspondance a tendance à en fabriquer une plausible
          plutôt qu'à déclarer qu'il n'en a pas — c'est pour cela que la consigne « n'invente aucune
          ligne » figure deux fois dans le prompt. Le symptôme est reconnaissable : un rapprochement
          trop propre, où la liste 3 est vide. Un mois sans aucune ligne sans correspondance est
          suspect, pas rassurant.
        </p>
      </div>

      <div className="apercu-part">
        <div className="k">Comment vérifier que ça marche</div>
        <p>
          Faites-lui traiter un mois que vous avez déjà bouclé à la main, et comparez. S'il retrouve
          vos écarts et n'en ajoute pas, la méthode tient. Sinon, c'est le format d'export qu'il faut
          corriger, pas le prompt.
        </p>
      </div>
      </details>

      <p className="apercu-pied">
        Les neuf autres actions ont cette longueur et cette structure, et portent sur vos tâches à
        vous.
      </p>
    </div>
  );
}
